// lib/adminTeachingMode.js
// Three-state teaching mode for the admin UX.
//
// States:
//   novice  — GraduationCap: Tour + all tooltips + teaching cards expanded (default)
//   expert  — Zap: tooltips on demand, cards collapsed, shortcut hints shown
//   focus   — Target: all teaching UI hidden (only ActionSheet + UndoToast remain)
//
// localStorage key: admin_teaching_mode
// Storage value: one of TEACHING_MODES (string)
// On bad / missing stored value → defaults to 'novice'.

export const TEACHING_MODES = Object.freeze(['novice', 'expert', 'focus'])

const STORAGE_KEY = 'admin_teaching_mode'
const DEFAULT_MODE = 'novice'
const CHANGE_EVENT = 'admin:teachingModeChange'

/**
 * Return the current teaching mode string.
 * Safe to call in SSR (typeof window check) — returns DEFAULT_MODE server-side.
 * @returns {'novice' | 'expert' | 'focus'}
 */
export function getTeachingMode() {
  if (typeof window === 'undefined') return DEFAULT_MODE
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored && TEACHING_MODES.includes(stored)) return stored
  return DEFAULT_MODE
}

/**
 * Persist a new teaching mode and dispatch a CustomEvent so all listeners
 * (AdminShell body-attribute sync, TeachingModeToggle display) update without
 * a page refresh.
 *
 * @param {'novice' | 'expert' | 'focus'} mode
 * @throws {Error} if mode is not in TEACHING_MODES
 */
export function setTeachingMode(mode) {
  if (!TEACHING_MODES.includes(mode)) {
    throw new Error(`Invalid teaching mode: "${mode}". Must be one of ${TEACHING_MODES.join(', ')}.`)
  }
  if (typeof window === 'undefined') return
  localStorage.setItem(STORAGE_KEY, mode)
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { mode } }))
}

/**
 * Subscribe to teaching mode changes.
 * Returns an unsubscribe function — call it to clean up (useEffect return).
 *
 * @param {(mode: 'novice' | 'expert' | 'focus') => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeTeachingMode(callback) {
  if (typeof window === 'undefined') return () => {}

  function handler(e) {
    callback(e.detail.mode)
  }

  window.addEventListener(CHANGE_EVENT, handler)
  return () => window.removeEventListener(CHANGE_EVENT, handler)
}
